import React, { useEffect, useMemo, useState } from "react";
import { Table, Typography } from "antd";
import { useSelector } from "react-redux";
import { generateColumns, parseDMY } from "../../../../utils/utilFunctions";
import { addMonths, format, parse } from "date-fns";
import { getProjectTimeline } from "../../../../utils/getProjectTimeline";

const { Text } = Typography;

const parseNum = (v) => {
        if (v === null || v === undefined) return 0;
        const n = Number(String(v).replace(/,/g, "").trim());
        return Number.isFinite(n) ? n : 0;
};

// Retained as a fallback for the very first render, before `systemDate` has
// loaded from Redux.
const getCurrentFYStart = () => {
        const today = new Date();
        return today.getMonth() >= 3 ? today.getFullYear() : today.getFullYear() - 1;
};

// NEW: FY start now comes from the admin-configured System Start Date
// (Redux `otherProjects.systemDate`, format DD-MM-YYYY) instead of today's date.
const parseSystemFYStart = (startDate) => {
        if (!startDate) return null;

        const [day, month, year] = startDate.split("-").map(Number);

        if (!day || !month || !year) return null;

        return month >= 4 ? year : year - 1; // Apr-Mar FY cycle
};

// OLD: CCI_UPTO was rendered straight off the API's static field, and the
// "Upto" title/columns were anchored to this project's own UPTO_DATE.
// NEW: Upto = the API's baseline CCI_UPTO plus every FY's "_formatted"
// value ("YYYY-YYYY_formatted") that falls strictly before the current
// (system-date-derived) FY - same "dynamic upto" rule used across the other
// reports/tables.
const getDynamicUpto = (row = {}, fyStart, baseField) => {
        let sum = parseNum(row[baseField]);

        Object.keys(row).forEach((key) => {
                const match = /^(\d{4})-\d{4}_formatted$/.exec(key);
                if (!match) return;

                const fyStartYear = Number(match[1]);
                if (fyStartYear < fyStart) {
                        sum += parseNum(row[key]);
                }
        });

        return sum.toFixed(2);
};

const ProjectRevenueTable = ({
        revenueData,
        headings = {
                firstHeading: "head",
                secondHeading: "Upto",
                thirdHeading: "Total",
        },
}) => {
        const { projectDates } = useSelector((state) => state.budget);
        const { systemDate } = useSelector((state) => state.otherProjects);
        const totalSum = "TOTAL";

        // OLD: const CURRENT_FY_START = getCurrentFYStart(); (fixed once at import time)
        // NEW: derive it from the admin-configured System Start Date, falling back
        // to today's date until systemDate has loaded.
        const CURRENT_FY_START = useMemo(
                () => parseSystemFYStart(systemDate?.STARTDATE) ?? getCurrentFYStart(),
                [systemDate]
        );

        // OLD: title was anchored to this project's own UPTO_DATE.
        // const formattedUptoDate = projectDates?.UPTO_DATE
        //         ? format(
        //                 parse(projectDates.UPTO_DATE, "dd-MM-yyyy", new Date()),
        //                 "MMM''yy"
        //         )
        //         : "";

        const uptoDateParsed = parseDMY(projectDates?.UPTO_DATE);
        const expectedDateParsed = parseDMY(projectDates?.EXPECTED_DATE);
        const unitPlanDateParsed = parseDMY(projectDates?.UNIT_PLAN_DATE);

        let startMonth = 4;
        let startYear = 2025;

        // OLD: rendered columns started the month after this project's own
        // UPTO_DATE.
        // NEW: anchored to the system-date-derived current FY (April of
        // CURRENT_FY_START) instead - consistent with the dynamic Upto value.
        // if (uptoDateParsed) {
        //         const startMonthDate = addMonths(uptoDateParsed, 1); // ✅ Move to next month
        //         startMonth = startMonthDate.getMonth() + 1; // 1-based month
        //         startYear = startMonthDate.getFullYear();
        // }
        startMonth = 4;
        startYear = CURRENT_FY_START;

        const { endYear, endMonth } = getProjectTimeline({
                zeroDate: expectedDateParsed || unitPlanDateParsed,
                expectedDate: expectedDateParsed,
                unitPlanDate: unitPlanDateParsed,
        });

        //     const endYear = expectedDateParsed
        //         ? expectedDateParsed.getFullYear()
        //         : 2026;
        //     const endMonth = expectedDateParsed
        //         ? expectedDateParsed.getMonth() + 1
        //         : 12;

        // let maxDataEndYear = 0;

        // if (revenueData) {
        //         const fyKeys = Object.keys(revenueData).filter(
        //                 (key) => /^\d{4}-\d{4}$/.test(key)
        //         );

        //         if (fyKeys.length > 0) {
        //                 maxDataEndYear = Math.max(
        //                         ...fyKeys.map((fy) => Number(fy.split("-")[1]))
        //                 );
        //         }
        // }

        // const expectedEndYear = expectedDateParsed
        //         ? expectedDateParsed.getFullYear()
        //         : 0;

        // const finalEndYear = Math.max(maxDataEndYear, expectedEndYear);

        // const unitPlanEndYear = unitPlanDateParsed
        //         ? unitPlanDateParsed.getFullYear()
        //         : 0;

        // const finalEndYear = unitPlanEndYear;

        // convert FY end → FY start
        // const endYear = finalEndYear ? finalEndYear - 1 : startYear + 1;

        // const endYear = finalEndYear || startYear;

        // const endMonth = unitPlanDateParsed
        // ? unitPlanDateParsed.getMonth() + 1
        // : 12;

        const [data, setData] = useState([]);



        let columns = [
                {
                        title: 'Description',
                        dataIndex: headings.firstHeading,
                        key: headings.firstHeading,
                        align: 'center',
                        render: (text, record, index) => {
                                if (record.render) {
                                        return record.render(text);
                                }

                                if (record.boldHeading) {
                                        return (
                                                <div className="w-[200px]">
                                                        <Text strong>{text}</Text> &nbsp;
                                                </div>
                                        );
                                }

                                if (record.link) {
                                        return (
                                                <div className="w-[200px]">
                                                        <a
                                                                href={record.link}
                                                                target="_blank"
                                                                rel="noopener noreferrer">
                                                                <Text
                                                                        strong
                                                                        className="text-[#466CE9] cursor-pointer underline">
                                                                        {text}
                                                                </Text>
                                                        </a>
                                                </div>
                                        );
                                }

                                return <div className="w-[200px]">{text}</div>;
                        },
                },
                {
                        // OLD: title: `Upto ${formattedUptoDate}`, (anchored to this project's own UPTO_DATE)
                        // NEW: anchored to the system-date-derived current FY.
                        title: `Upto Mar'${String(CURRENT_FY_START).slice(-2)}`,
                        dataIndex: 'CCI_UPTO',
                        key: 'CCI_UPTO',
                        align: 'center',
                        render: (text) => {
                                return <div className="text-right">{text}</div>;
                        },
                },
        ];

        useEffect(() => {
                if (revenueData) {
                        // OLD: CCI_UPTO was rendered straight off the API's static field.
                        // NEW: replace it with the dynamic value first.
                        setData([
                                {
                                        key: "2",
                                        head: "Total Revenue",
                                        boldHeading: true,
                                        ...revenueData,
                                        CCI_UPTO: getDynamicUpto(revenueData, CURRENT_FY_START, "CCI_UPTO"),
                                },
                        ]);
                }
        }, [revenueData, CURRENT_FY_START]);

        const finalEndDateRaw = expectedDateParsed && unitPlanDateParsed
                ? (expectedDateParsed > unitPlanDateParsed
                        ? projectDates?.EXPECTED_DATE
                        : projectDates?.UNIT_PLAN_DATE)
                : (projectDates?.EXPECTED_DATE || projectDates?.UNIT_PLAN_DATE);

        return (
                <Table
                        columns={generateColumns(
                                startYear,
                                endYear,
                                startMonth,
                                endMonth,
                                columns,
                                finalEndDateRaw,
                                totalSum,
                                true,
                        )}
                        dataSource={data}
                        pagination={false}
                        bordered
                        scroll={{ x: 'max-content' }}
                        className={` custom-ant-table`}
                        rowClassName={(record, index) => {
                                if (index === 0) {
                                        return 'table-row-dark';
                                } else {
                                        return 'table-row-light';
                                }
                        }}
                />
        );
};

export default ProjectRevenueTable;
